import './styles.css';
import { readBestScore, recordBestScore, type BestScoreStore } from '../engine/best-score';
import { additionCountBounds } from '../engine/question';
import {
  completeSession,
  recordAnswer,
  startFollowUp,
  startTest,
  type LiveSession,
  type SessionResult
} from '../engine/session';
import type { CompletedAnswer } from '../engine/types';
import { listYearConfigs, type YearConfig, type YearKey } from '../engine/year-config';

type View = 'landing' | 'home' | 'test' | 'results';

interface YearOption {
  key: YearKey;
  label: string;
  shortLabel: string;
  description: string;
  timeLimitSeconds: number;
  questionCount: number;
  additionCount: number;
  multiplicationCount: number;
  mixAdjustable: boolean;
  defaultAdditionRatio: number;
  additionBounds: { min: number; max: number };
}

interface ResultView extends SessionResult {
  isNewBest: boolean;
}

interface AppState {
  view: View;
  years: YearOption[];
  year: YearKey | null;
  mixAddition: number;
  session: LiveSession | null;
  answers: CompletedAnswer[];
  result: ResultView | null;
  bestScore: number;
  error: string;
  questionStartedAt: number;
  currentIndex: number;
  trail: Array<{ text: string }>;
  answer: string;
  submitting: boolean;
}

const SELECTED_YEAR_KEY = 'bmbt:selectedYear';
const mixStorageKey = (year: string): string => `bmbt:mix:${year}`;

const mount = document.querySelector<HTMLElement>('#app');
if (!mount) {
  throw new Error('Missing #app mount point');
}
const root: HTMLElement = mount;

// Storage is best-effort: it can throw in private/sandboxed browsers, and the
// only things stored are anonymous settings + best score, never personal data.
const safeStorage: BestScoreStore & { removeItem(key: string): void } = {
  getItem(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      // storage unavailable
    }
  },
  removeItem(key) {
    try {
      localStorage.removeItem(key);
    } catch {
      // storage unavailable
    }
  }
};

function toYearOption(config: YearConfig): YearOption {
  return {
    key: config.key,
    label: config.label,
    shortLabel: config.shortLabel,
    description: config.description,
    timeLimitSeconds: config.timeLimitSeconds,
    questionCount: config.questionCount,
    additionCount: config.additionCount,
    multiplicationCount: config.multiplicationCount,
    mixAdjustable: config.mixAdjustable,
    defaultAdditionRatio: config.additionCount / config.questionCount,
    additionBounds: additionCountBounds(config)
  };
}

const state: AppState = {
  view: 'landing',
  years: listYearConfigs().map(toYearOption),
  year: null,
  mixAddition: 50,
  session: null,
  answers: [],
  result: null,
  bestScore: 0,
  error: '',
  questionStartedAt: 0,
  currentIndex: 0,
  trail: [],
  answer: '',
  submitting: false
};

function findYear(key: YearKey | null): YearOption | null {
  return state.years.find((year) => year.key === key) ?? null;
}

function currentYear(): YearOption | null {
  return findYear(state.year);
}

function mixForYear(option: YearOption): number {
  const stored = safeStorage.getItem(mixStorageKey(option.key));
  if (stored !== null && Number.isFinite(Number(stored))) {
    return Number(stored);
  }
  return Math.round(option.defaultAdditionRatio * 100);
}

function setState(patch: Partial<AppState>): void {
  const previousView = state.view;
  Object.assign(state, patch);
  render();
  if (state.view !== previousView) {
    syncHashToState({ replace: previousView === 'test' });
  }
}

function render(): void {
  const views: Record<View, () => string> = {
    landing: renderLanding,
    home: renderHome,
    test: renderTest,
    results: renderResults
  };
  root.innerHTML = `<main class="app">${views[state.view]()}</main>`;
  bindEvents();
}

function header(subtitle = 'Beat That!', stamp = 'Big Maths · Beat That'): string {
  return `
    <header class="header">
      <button class="brand brand-button" data-action="brand-home" aria-label="Big Maths Beat That home">
        <div class="brand-mark">B</div>
        <div>
          <div class="brand-title">Big Maths</div>
          <div class="muted">${escapeHtml(subtitle)}</div>
        </div>
      </button>
      <span class="stamp">${escapeHtml(stamp)}</span>
    </header>
  `;
}

function renderLanding(): string {
  const cards = state.years
    .map((year) => {
      const isLast = year.key === state.year;
      return `
        <button class="card year-card ${isLast ? 'is-last' : ''}" data-year="${year.key}">
          <div class="year-card-top">
            <span class="stamp year-stamp">${escapeHtml(year.shortLabel)}</span>
            ${isLast ? '<span class="year-last">Last played</span>' : ''}
          </div>
          <div class="display year-name">${escapeHtml(year.label)}</div>
          <div class="muted year-desc">${escapeHtml(year.description)}</div>
          <div class="year-meta">
            <span class="mono">${year.questionCount} questions</span>
            <span class="mono">${year.timeLimitSeconds}s</span>
          </div>
        </button>
      `;
    })
    .join('');
  return `
    ${header('Beat That!')}
    <section class="shell">
      <div class="results-head">
        <div>
          <div class="eyebrow">Big Maths Beat That</div>
          <h1 class="display">Pick your year</h1>
        </div>
      </div>
      ${state.error ? `<div class="error">${escapeHtml(state.error)}</div>` : ''}
      <div class="grid year-grid">
        ${cards}
      </div>
    </section>
  `;
}

function exampleFor(year: YearOption): { operand1: number; operator: string; operand2: number } {
  if (year.additionCount === 0) {
    return { operand1: 7, operator: 'x', operand2: 3 };
  }
  if (year.multiplicationCount === 0) {
    return { operand1: 4, operator: '+', operand2: 4 };
  }
  return { operand1: 8, operator: '+', operand2: 7 };
}

function mixSummary(year: YearOption): string {
  const parts: string[] = [];
  if (year.additionCount) {
    parts.push(`${year.additionCount} +`);
  }
  if (year.multiplicationCount) {
    parts.push(`${year.multiplicationCount} ×`);
  }
  return parts.join(' · ');
}

function renderHome(): string {
  const year = currentYear();
  if (!year) {
    return renderLanding();
  }
  const example = exampleFor(year);
  const minPercent = Math.round((year.additionBounds.min / year.questionCount) * 100);
  const maxPercent = Math.round((year.additionBounds.max / year.questionCount) * 100);
  const bestLine = state.bestScore > 0
    ? `Your best is <b>${state.bestScore}/${year.questionCount}</b> — beat it!`
    : `Answer all ${year.questionCount} to set your first best.`;

  const mixCard = year.mixAdjustable
    ? `
        <div class="range-line">
          <label for="mix">The mix</label>
          <span class="mono muted" id="mix-readout">${100 - state.mixAddition}% × · ${state.mixAddition}% +</span>
        </div>
        <input id="mix" type="range" min="${minPercent}" max="${maxPercent}" step="1" value="${state.mixAddition}" />
        <div class="notice">${year.questionCount} questions every time. The default mix matches the ${escapeHtml(year.label)} Beat That card.</div>
      `
    : `
        <div class="range-line">
          <label>This test</label>
          <span class="mono muted">${escapeHtml(mixSummary(year))}</span>
        </div>
        <div class="notice">${year.questionCount} questions in ${year.timeLimitSeconds} seconds, fixed for ${escapeHtml(year.label)}.</div>
      `;

  return `
    ${header(`${year.label} · Beat That`, `${year.shortLabel} · Beat That`)}
    <section class="shell grid home-grid">
      <div class="card hero">
        <div class="eyebrow">${escapeHtml(year.label)} · Total Recall</div>
        <h1 class="display">Ready,<br><span style="color:var(--terra)">set,</span> sums?</h1>
        <p class="muted">${year.questionCount} questions in ${year.timeLimitSeconds} seconds. Type the answer and hit <span class="mono">Enter</span>. Timing appears once every question is answered.</p>
        <div class="example" aria-hidden="true">
          <div class="display sum">${example.operand1} ${example.operator} ${example.operand2}</div>
          <div class="display sum" style="color:var(--terra)">=</div>
          <div>
            <div class="eyebrow">your answer</div>
            <div class="answer-box display">?</div>
          </div>
        </div>
        <div class="button-row">
          <button class="btn terra" data-action="start">Start the drill</button>
          <button class="btn ghost" data-action="landing">Change year</button>
          <span class="muted">${bestLine}</span>
        </div>
      </div>
      <aside class="grid">
        <div class="card controls">
          ${mixCard}
        </div>
        ${state.error ? `<div class="error">${escapeHtml(state.error)}</div>` : ''}
      </aside>
    </section>
  `;
}

function renderTest(): string {
  const session = state.session;
  if (!session) {
    return renderLanding();
  }
  const question = session.questions[state.currentIndex];
  const year = findYear(session.year);
  const subtitle = session.type === 'followup' ? 'Follow-up practice' : `${year?.label ?? ''} · Beat That`;
  return `
    ${header(subtitle, `${year?.shortLabel ?? 'Big Maths'} · Beat That`)}
    <section class="shell">
      <div class="topline quiet-progress">
        <div>
          <div class="eyebrow" style="text-align:center">Question ${state.currentIndex + 1} of ${session.questions.length}</div>
          <div class="dots">${session.questions.map((_, index) => `<span class="dot ${index < state.currentIndex ? 'done' : ''} ${index === state.currentIndex ? 'now' : ''}"></span>`).join('')}</div>
        </div>
        <button class="btn ghost" data-action="quit">Quit</button>
      </div>
      <div class="card test-card">
        <div class="stamp-trail">
          ${state.trail.slice(-7).map((entry) => `<span class="stamp mini-stamp">${escapeHtml(entry.text)}</span>`).join('')}
        </div>
        <div class="question-stage">
          <form data-form="answer">
            <div class="question-line">
              <div class="question-group">
                <span class="display question-number">${question.operand1}</span>
                <span class="display question-op">${question.operator}</span>
                <span class="display question-number">${question.operand2}</span>
              </div>
              <span class="display question-op" style="color:rgba(24,21,18,.35)">=</span>
              <input id="answer" class="answer-input" type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="off" value="${escapeAttr(state.answer)}" autofocus />
            </div>
          </form>
        </div>
      </div>
    </section>
  `;
}

function renderResults(): string {
  const result = state.result;
  if (!result) {
    return renderLanding();
  }
  const maxTime = Math.max(
    1,
    ...result.slowest.map((item) => item.timeMs ?? 0),
    ...result.answers.map((answer) => answer.timeMs ?? 0)
  );
  return `
    ${header('Results', `${result.yearLabel} · Beat That`)}
    <section class="shell">
      <div class="results-head">
        <div>
          <div class="eyebrow">${result.type === 'followup' ? 'Follow-up complete' : 'Speed test complete'}</div>
          <h1 class="display">${result.score}/${result.questionCount}${result.isNewBest ? ' · new best!' : ''}</h1>
        </div>
        <div class="button-row" style="margin:0;padding:0">
          <button class="btn sage" data-action="followup">Start follow-up</button>
          <button class="btn ghost" data-action="landing">Home</button>
        </div>
      </div>
      <div class="stats">
        <div class="card stat"><div class="eyebrow">Score</div><div class="display value">${result.score}/${result.questionCount}</div></div>
        <div class="card stat"><div class="eyebrow">Total time</div><div class="display value">${(result.totalTimeMs / 1000).toFixed(1)}s</div></div>
        <div class="card stat"><div class="eyebrow">Focus area</div><div class="display" style="font-size:30px">${escapeHtml(result.focusArea || 'Mixed facts')}</div></div>
      </div>
      <div class="grid results-grid">
        <div class="card panel">
          <h2 class="display">Answer table</h2>
          <table class="answer-table">
            <thead><tr><th>#</th><th>Question</th><th>Your answer</th><th>Correct</th><th>Time</th></tr></thead>
            <tbody>
              ${result.answers.map((answer) => `
                <tr class="${answer.isSlow ? 'slow' : ''} ${answer.isCorrect ? '' : 'wrong'}">
                  <td>${answer.position}</td>
                  <td>${answer.operand1} ${answer.operator} ${answer.operand2}</td>
                  <td>${answer.givenAnswer ?? '-'}</td>
                  <td>${answer.correctAnswer}</td>
                  <td>${answer.timeMs == null ? '-' : (answer.timeMs / 1000).toFixed(1) + 's'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        <aside class="grid">
          <div class="card panel">
            <h2 class="display">Coach's note</h2>
            <div class="notice">${escapeHtml(result.diagnosis)}</div>
          </div>
          <div class="card panel">
            <h2 class="display">Slowest 5</h2>
            <div class="bar-chart">
              ${result.slowest.map((item) => `
                <div class="bar-row">
                  <span>${escapeHtml(item.question)}</span>
                  <span class="bar-track"><span class="bar-fill ${item.isCorrect ? '' : 'slow'}" style="--w:${Math.max(8, ((item.timeMs ?? 0) / maxTime) * 100)}%"></span></span>
                  <span class="mono">${item.timeMs == null ? '-' : (item.timeMs / 1000).toFixed(1) + 's'}</span>
                </div>
              `).join('')}
            </div>
          </div>
        </aside>
      </div>
    </section>
  `;
}

function bindEvents(): void {
  document.querySelectorAll<HTMLElement>('[data-year]').forEach((button) => {
    const key = button.dataset.year;
    if (key) {
      button.addEventListener('click', () => selectYear(key));
    }
  });
  bindClick('[data-action="brand-home"]', () =>
    setState({ view: 'landing', session: null, answers: [], answer: '', error: '' })
  );
  bindClick('[data-action="start"]', startSession);
  bindClick('[data-action="landing"]', () =>
    setState({ view: 'landing', session: null, answers: [], answer: '', error: '' })
  );
  bindClick('[data-action="quit"]', () =>
    setState({ view: 'home', session: null, answers: [], answer: '' })
  );
  bindClick('[data-action="followup"]', startFollowUpSession);

  document.querySelector<HTMLInputElement>('#mix')?.addEventListener('input', (event) => {
    const target = event.target as HTMLInputElement;
    const value = Number(target.value);
    state.mixAddition = value;
    if (state.year) {
      safeStorage.setItem(mixStorageKey(state.year), String(value));
    }
    // Update the readout in place so the slider keeps focus for keyboard users.
    const readout = document.querySelector<HTMLElement>('#mix-readout');
    if (readout) {
      readout.textContent = `${100 - value}% × · ${value}% +`;
    }
  });

  document.querySelector<HTMLFormElement>('[data-form="answer"]')?.addEventListener('submit', submitAnswer);

  const answer = document.querySelector<HTMLInputElement>('#answer');
  answer?.addEventListener('input', (event) => {
    const target = event.target as HTMLInputElement;
    const digitsOnly = target.value.replace(/\D/g, '');
    target.value = digitsOnly;
    state.answer = digitsOnly;
  });
  answer?.focus();
}

function bindClick(selector: string, handler: () => void): void {
  document.querySelector<HTMLElement>(selector)?.addEventListener('click', handler);
}

function selectYear(key: string): void {
  const option = state.years.find((year) => year.key === key);
  if (!option) {
    return;
  }
  safeStorage.setItem(SELECTED_YEAR_KEY, key);
  setState({
    year: option.key,
    mixAddition: mixForYear(option),
    bestScore: readBestScore(safeStorage, option.key),
    view: 'home',
    error: ''
  });
}

function startSession(): void {
  const year = currentYear();
  if (!year) {
    setState({ view: 'landing', error: 'Choose a year first.' });
    return;
  }
  beginLiveSession(startTest(year.key, { additionRatio: state.mixAddition / 100 }));
}

function startFollowUpSession(): void {
  if (!state.result) {
    return;
  }
  beginLiveSession(startFollowUp(state.result));
}

function beginLiveSession(session: LiveSession): void {
  state.questionStartedAt = performance.now();
  setState({
    view: 'test',
    session,
    answers: [],
    currentIndex: 0,
    trail: [],
    answer: '',
    error: ''
  });
}

function submitAnswer(event: Event): void {
  event.preventDefault();
  const session = state.session;
  if (state.answer === '' || state.submitting || !session) {
    return;
  }
  state.submitting = true;
  const question = session.questions[state.currentIndex];
  const givenAnswer = Number(state.answer);
  const timeMs = performance.now() - state.questionStartedAt;
  state.answers.push(recordAnswer(question, givenAnswer, timeMs));
  state.trail.push({ text: `${question.operand1} ${question.operator} ${question.operand2}` });

  if (state.currentIndex >= session.questions.length - 1) {
    state.submitting = false;
    void finishSession();
    return;
  }
  state.currentIndex += 1;
  state.answer = '';
  state.questionStartedAt = performance.now();
  state.submitting = false;
  render();
}

async function finishSession(): Promise<void> {
  const session = state.session;
  if (!session) {
    return;
  }
  try {
    const result = await completeSession(session, state.answers);
    const isNewBest = result.type === 'test' && recordBestScore(safeStorage, result.year, result.score);
    setState({
      view: 'results',
      result: { ...result, isNewBest },
      bestScore: readBestScore(safeStorage, result.year),
      session: null
    });
  } catch (error) {
    setState({
      view: 'landing',
      session: null,
      error: error instanceof Error ? error.message : 'Something went wrong'
    });
  }
}

function clearLegacyData(): void {
  // The old server-backed app stored a pupil name and a single (non-year) best.
  // This build collects no personal data, so purge any leftovers (best-effort).
  safeStorage.removeItem('bigMathsChildName');
  safeStorage.removeItem('bigMathsBestScore');
}

function escapeHtml(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#039;';
    }
  });
}

function escapeAttr(value: unknown): string {
  return escapeHtml(value).replace(/`/g, '&#096;');
}

const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea, label, [data-action], [data-year]';
root.addEventListener('mousedown', (event) => {
  if (state.view !== 'test') {
    return;
  }
  const target = event.target as HTMLElement;
  if (target.closest(INTERACTIVE_SELECTOR)) {
    return;
  }
  const answer = document.querySelector<HTMLInputElement>('#answer');
  if (!answer) {
    return;
  }
  event.preventDefault();
  answer.focus();
});

let suppressHashSync = false;

function buildHash(view: View): string {
  switch (view) {
    case 'home':
      return '#/home';
    case 'test':
      return '#/test';
    case 'results':
      return '#/results';
    default:
      return '#/years';
  }
}

function parseHash(): View {
  const match = (window.location.hash || '').match(/^#\/(years|home|test|results)/);
  if (!match) {
    return 'landing';
  }
  return match[1] === 'years' ? 'landing' : (match[1] as View);
}

function syncHashToState({ replace = false }: { replace?: boolean } = {}): void {
  if (suppressHashSync) {
    return;
  }
  const target = buildHash(state.view);
  if (window.location.hash === target) {
    return;
  }
  const historyState = { view: state.view };
  if (replace) {
    window.history.replaceState(historyState, '', target);
  } else {
    window.history.pushState(historyState, '', target);
  }
}

function resetToLanding(): void {
  window.history.replaceState({ view: 'landing' }, '', buildHash('landing'));
  setState({ view: 'landing' });
}

function applyPoppedRoute(view: View): void {
  if (view === 'home') {
    if (state.year) {
      setState({ view: 'home' });
    } else {
      resetToLanding();
    }
    return;
  }
  if (view === 'test') {
    if (state.session) {
      setState({ view: 'test' });
    } else {
      resetToLanding();
    }
    return;
  }
  if (view === 'results') {
    if (state.result) {
      setState({ view: 'results' });
    } else {
      resetToLanding();
    }
    return;
  }
  setState({ view: 'landing', session: null, answers: [], answer: '' });
}

window.addEventListener('popstate', () => {
  suppressHashSync = true;
  try {
    applyPoppedRoute(parseHash());
  } finally {
    suppressHashSync = false;
  }
});

function init(): void {
  clearLegacyData();
  const storedYear = safeStorage.getItem(SELECTED_YEAR_KEY);
  const option = storedYear ? state.years.find((year) => year.key === storedYear) : undefined;
  if (option) {
    state.year = option.key;
    state.mixAddition = mixForYear(option);
    state.bestScore = readBestScore(safeStorage, option.key);
  }
  const view: View = parseHash() === 'home' && state.year ? 'home' : 'landing';
  window.history.replaceState({ view }, '', buildHash(view));
  state.view = view;
  render();
}

init();
